import { untracked, makeObservable, computed, observable, action, runInAction } from 'mobx'
import { fromPromise, IPromiseBasedObservable, isPromiseBasedObservable } from 'mobx-utils'
function isPromiseLike (result) {
  return result && typeof result.then === 'function'
}

function error (error) {
  return { ok: false, error }
}

function value<T = any> (value: T) {
  return { ok: true, value }
}

interface PromisedComputedOpts {
  pausedFn?: () => boolean
  [k: string]: unknown
}
export class PromisedComputed<T> {
  fetch: () => T|Promise<T>
  cached: ReturnType<typeof value>|ReturnType<typeof error>
  paused: () => boolean
  pr: IPromiseBasedObservable<ReturnType<typeof value>|ReturnType<typeof error>>

  constructor (init: T, fetch: () => T|Promise<T>, {pausedFn, ...cfg}: PromisedComputedOpts = {pausedFn: undefined}) {
    makeObservable(this, {
      refreshCallCount: observable,
      currentState: computed,
      busy: computed,
      refresh: action,
      value: computed
    }, cfg)
    this.fetch = fetch
    runInAction(() => { this.refreshCallCount = 0 })
    this.cached = value(init)
    this.paused = pausedFn
  }

  refreshCallCount = 0

  fromPromise(pr: Promise<T>) {
    return untracked(() => {
      this.pr = fromPromise(pr.then(value, e => error(e)), this.pr as any)
      return this.pr
    })
  }

  get currentState () {
    try {
      if (this.paused && this.paused()) return this.cached
      this.refreshCallCount
      const promiseOrValue = this.fetch()

      return isPromiseLike(promiseOrValue)
        ? this.fromPromise(promiseOrValue as Promise<T>)
        : value(promiseOrValue)
    } catch (x) {
      return error(x)
    }
  }

  get busy () {
    const s = this.currentState
    return !!(isPromiseBasedObservable(s) && s.state === 'pending')
  }

  refresh () {
    this.refreshCallCount++
  }

  get () {
    try {
      return this.value
    } catch (e) {
      return this.cached
    }
  }

  get value () {
    const s = this.currentState

    const r = !isPromiseBasedObservable(s) ? s
            : s.state === 'fulfilled' ? s.value
            : this.cached

    this.cached = r

    if (r.ok) {
      return (r as any).value as T
    } else {
      throw (r as any).error as unknown
    }
  }
}

export function promisedComputed<T>(init: T, compute: () => T|Promise<T>, conf?: PromisedComputedOpts) {
  return new PromisedComputed(init, compute, conf)
}
